import React from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { Helmet } from "react-helmet-async";

/**
 * Schema.org Organization markup for Armstrong K9 Registry LLC.
 *
 * Why it matters:
 *   • Google Knowledge Graph + Bing pull these fields to display the
 *     business in search results (logo, hours, phone).
 *   • D&B verification scrapers ingest schema.org markup as one of the
 *     signals when deciding if "this is a real, reachable business" —
 *     specifically the @type, address, telephone, email, and
 *     foundingDate fields.
 *   • Apple Sign In, Stripe Connect, and Plaid all give bonus trust to
 *     domains with a well-formed Organization schema.
 *
 * Source of truth for the values:
 *   `frontend/.env` → REACT_APP_LEGAL_ENTITY, REACT_APP_BUSINESS_PHONE,
 *   REACT_APP_BUSINESS_ADDRESS, REACT_APP_SUPPORT_EMAIL.
 *
 * Mount this once at the top of LandingPage (the homepage) and any other
 * root public pages. react-helmet-async dedupes across mount points, so
 * dropping it in About + Contact is also safe.
 */
const LEGAL_ENTITY =
  process.env.REACT_APP_LEGAL_ENTITY || "Armstrong K9 Registry";
const DBA_NAME = process.env.REACT_APP_DBA_NAME || LEGAL_ENTITY;
const BUSINESS_PHONE = process.env.REACT_APP_BUSINESS_PHONE || "";
const BUSINESS_PHONE_E164 = BUSINESS_PHONE
  ? BUSINESS_PHONE.startsWith("+")
    ? BUSINESS_PHONE.replace(/[^\d+]/g, "")
    : "+1" + BUSINESS_PHONE.replace(/[^\d]/g, "")
  : "";
const BUSINESS_EMAIL =
  process.env.REACT_APP_SUPPORT_EMAIL || "hello@armstrongk9registry.com";
const BUSINESS_ADDRESS = process.env.REACT_APP_BUSINESS_ADDRESS || "";

function parseAddress(raw) {
  // Best-effort parser. "4054 Highway 273, Anderson, CA 96007"
  // becomes streetAddress="4054 Highway 273", addressLocality="Anderson",
  // addressRegion="CA", postalCode="96007", addressCountry="US".
  if (!raw) return null;
  const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length < 3) return { streetAddress: raw, addressCountry: "US" };
  const [street, city, stateZip, ...rest] = parts;
  const m = (stateZip || "").match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  return {
    "@type": "PostalAddress",
    streetAddress: street,
    addressLocality: city,
    addressRegion: m ? m[1].toUpperCase() : undefined,
    postalCode: m ? m[2] : undefined,
    addressCountry: rest[0] || "US",
  };
}

export default function OrganizationSchema() {
  const origin =
    typeof window !== "undefined" && window.location
      ? webOrigin()
      : "https://armstrongk9registry.com";
  const address = parseAddress(BUSINESS_ADDRESS);
  const ld = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${origin}/#organization`,
    name: DBA_NAME,
    legalName: LEGAL_ENTITY,
    alternateName: LEGAL_ENTITY === DBA_NAME ? undefined : LEGAL_ENTITY,
    foundingDate: "2026-05-22",
    foundingLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressRegion: "CA",
        addressCountry: "US",
      },
    },
    // Business classification codes — match these to whatever you put
    // on the D-U-N-S application. D&B's automated scrapers cross-check
    // the classification on file against what they can discover on the
    // public website; matching codes is one of the trust signals they
    // use to decide if "this business is what it says it is".
    //   • SIC 0752   — Animal Specialty Services (except Veterinary)
    //   • NAICS 812910 — Pet Care (except Veterinary) Services
    //   • ISIC v4 9609 — Other personal service activities n.e.c.
    naics: "812910",
    isicV4: "9609",
    knowsAbout: [
      "Dog breeding",
      "Pedigree registry",
      "Canine genetics",
      "Dog show competitions",
      "Working and sport dogs",
      "Responsible breeding practices",
    ],
    url: origin,
    logo: `${origin}/logo-512.png`,
    image: `${origin}/logo-512.png`,
    email: BUSINESS_EMAIL,
    ...(BUSINESS_PHONE_E164 && { telephone: BUSINESS_PHONE_E164 }),
    ...(address && { address }),
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: BUSINESS_EMAIL,
        ...(BUSINESS_PHONE_E164 && { telephone: BUSINESS_PHONE_E164 }),
        availableLanguage: ["English"],
        areaServed: ["US", "CA"],
      },
    ],
    sameAs: [
      "https://www.facebook.com/ArmstrongK9Registry",
      "https://www.instagram.com/armstrongk9registry",
    ],
    // Business hours machine-readable form
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "09:00",
        closes: "17:00",
      },
    ],
  };

  // Strip undefined keys so JSON-LD validates cleanly.
  const clean = JSON.parse(JSON.stringify(ld));

  return (
    <Helmet>
      <script
        type="application/ld+json"
        data-testid="organization-schema-ld"
      >{JSON.stringify(clean)}</script>
    </Helmet>
  );
}
